import React from "react";
import { ResponsiveContainer, Area, AreaChart } from "recharts";
import { Card } from "@/shared/components/ui/Card";
import { MetricCardProps } from "../types";
import { Icon } from '@/shared/icons'
import type { IconName } from '@/shared/icons'

/**
 * Coluna de rótulo + valor da variante dual (ver `hasDualValue` abaixo).
 * Local ao módulo — elimina a duplicação verbatim entre as colunas
 * primary/secondary (mesmo par de classes/CSS vars, só troca testid/label/value).
 */
const MetricColumn: React.FC<{
  testid: string;
  label: React.ReactNode;
  value: React.ReactNode;
}> = ({ testid, label, value }) => (
  <div className="flex-1 min-w-0" data-testid={testid}>
    <div
      className="text-[10px] font-semibold uppercase tracking-[0.06em] truncate"
      style={{ color: 'var(--theme-muted-text)' }}
    >
      {label}
    </div>
    <div
      className="text-[20px] @kpi-mid:text-[22px] @kpi:text-[26px] leading-none font-bold tracking-tight mt-[5px]"
      style={{ color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}
    >
      {value}
    </div>
  </div>
);

/**
 * Card de KPI do dashboard — anatomia do Design System.
 *
 * Topo: chip de ícone neutro + título. Meio: valor grande + pill de delta
 * (verde ↑ / vermelho ↓ / neutro). Sparkline de tendência inline.
 * Rodapé: comparação com período anterior.
 *
 * Variante de 2 valores (ativada quando `secondaryValue !== undefined`, ex:
 * "Atendimentos" = criados + finalizados): substitui o bloco de valor único
 * por duas colunas de peso igual (`MetricColumn`), cada uma com rótulo
 * próprio (`valueLabel`/`secondaryLabel`), sempre lado a lado, separadas por
 * um divisor vertical — sem delta pill nesse modo (as duas leituras já são a
 * comparação), mas COM sparkline como nos demais cards. A fonte do valor
 * escalona em 3 degraus (20/22/26px) via container query sobre o miolo do
 * card (`kpi-mid`/`kpi`) — encolhe só quando o espaço aperta, nunca empilha.
 *
 * Preserva todos os campos de `DashboardMetric` (value string cru, change,
 * trend, changePercent, comparisonValue, sparklineData) sem inventar dados.
 *
 * @example
 * <MetricCard metric={metric} onClick={(id) => ...} />
 */
export const MetricCard: React.FC<MetricCardProps> = ({
  metric,
  onClick,
}) => {
  const {
    title, value, change, trend, icon, id,
    sparklineData, changePercent, comparisonValue,
    valueLabel, secondaryValue, secondaryLabel,
  } = metric;

  const getTrendConfig = () => {
    switch (trend) {
      case 'up':
        return {
          color: 'var(--theme-success-color)',
          bgColor: 'var(--theme-success-background, rgba(16, 185, 129, 0.12))',
          TrendIcon: 'arrow-up-right' as IconName,
          sparkColor: 'var(--theme-success-color)',
        };
      case 'down':
        return {
          color: 'var(--theme-error-color)',
          bgColor: 'var(--theme-error-background, rgba(239, 68, 68, 0.12))',
          TrendIcon: 'arrow-down-right' as IconName,
          sparkColor: 'var(--theme-error-color)',
        };
      default:
        return {
          color: 'var(--theme-muted-text)',
          bgColor: 'var(--theme-button-ghost-hover)',
          TrendIcon: 'minus' as IconName,
          sparkColor: 'var(--theme-muted-text)',
        };
    }
  };

  const handleClick = () => {
    if (onClick) onClick(id);
  };

  const trendConfig = getTrendConfig();

  // Card de 2 métricas (ex: "Atendimentos" = criados + finalizados). O bloco
  // duplo substitui o valor único e dispensa a delta pill — as duas leituras
  // já são a comparação.
  const hasDualValue = secondaryValue !== undefined;

  // Preparar dados do sparkline
  const sparkData = sparklineData?.map((v, i) => ({ value: v, index: i })) || [];
  const hasSparkline = sparkData.length > 0;

  // Formatar percentual de mudança — só mostra pill quando houver percentual real
  const displayPercent = changePercent !== undefined
    ? `${changePercent > 0 ? '+' : ''}${changePercent}%`
    : null;

  return (
    <Card
      hoverable
      data-testid={`metric-card-${id}`}
      className={`h-full flex flex-col overflow-hidden ${onClick ? 'cursor-pointer' : ''}`}
      onClick={handleClick}
    >
      {/* Topo: título à esquerda, chip de ícone à direita.
          O título ancora na mesma borda esquerda do valor e do rodapé, dando uma
          coluna de leitura única; o ícone vira marcador no canto oposto.
          `min-w-0` no título é o que permite o `truncate` funcionar — sem ele o
          item flex não encolhe abaixo do conteúdo e empurraria o chip para fora. */}
      <div className="flex items-center justify-between gap-2.5 px-4 pt-3.5 pb-1 min-w-0">
        <h3
          className="text-xs font-semibold truncate min-w-0"
          style={{ color: 'var(--theme-muted-text)' }}
        >
          {title}
        </h3>
        <div
          className="w-[30px] h-[30px] rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: 'var(--theme-surface-sunken)' }}
        >
          <Icon name={icon} className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
        </div>
      </div>

      {/* Valor: bloco duplo (2 métricas) ou valor único + delta pill */}
      {hasDualValue ? (
        <div className="px-4 pb-1 @container">
          <div className="flex items-stretch gap-3.5">
            <MetricColumn testid={`metric-card-${id}-primary`} label={valueLabel} value={value} />

            {/* Divisor sempre vertical — as duas colunas ficam sempre lado a lado */}
            <div
              className="w-px flex-shrink-0"
              style={{ backgroundColor: 'var(--theme-card-border)' }}
            />

            <MetricColumn testid={`metric-card-${id}-secondary`} label={secondaryLabel} value={secondaryValue} />
          </div>
        </div>
      ) : (
        <div className="px-4 pb-1">
          <div className="flex items-end justify-between gap-2">
            <span
              className="text-[26px] leading-none font-bold tracking-tight"
              style={{
                color: 'var(--theme-card-text)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {value}
            </span>

            {/* Delta pill */}
            {displayPercent && (
              <span
                className="inline-flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
                style={{
                  color: trendConfig.color,
                  backgroundColor: trendConfig.bgColor,
                }}
              >
                <Icon name={trendConfig.TrendIcon} className="w-3 h-3" />
                {displayPercent}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Sparkline */}
      {hasSparkline && (
        <div className="px-2 py-1 flex-1 min-h-0" style={{ minHeight: '38px' }}>
          <ResponsiveContainer width="100%" height={38}>
            <AreaChart data={sparkData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
              <defs>
                <linearGradient id={`sparkGradient-${id}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={trendConfig.sparkColor} stopOpacity={0.22} />
                  <stop offset="100%" stopColor={trendConfig.sparkColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="value"
                stroke={trendConfig.sparkColor}
                strokeWidth={1.6}
                fill={`url(#sparkGradient-${id})`}
                dot={false}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Comparação / texto de mudança — mt-auto empurra pro rodapé quando o
          Card (h-full flex flex-col) não tem sparkline pra absorver a sobra
          vertical. Inofensivo quando o sparkline existe (caso comum a todos
          os cards, incl. a variante dual): o flex-1 dele já consumiu o
          espaço disponível. */}
      <div className="px-4 pb-3.5 pt-1 mt-auto">
        {comparisonValue ? (
          <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
            <span style={{ color: trendConfig.color, fontWeight: 500 }}>{comparisonValue}</span>
            {' '}vs período anterior
          </p>
        ) : (
          <p className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
            {change}
          </p>
        )}
      </div>
    </Card>
  );
};

MetricCard.displayName = 'MetricCard';
